import React from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, ArrowLeft } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import ComparisonSummary from './ComparisonSummary'
import SourcingComparison from './SourcingComparison'
import SourcingEdit from './SourcingEdit'
import SaveReportModal from './SaveReportModal'
import { Product, OptionsBundle } from '../../stores/appReportsStore'
import { greenButtonClass, greyButtonClass } from '../../styling/stylingHelper'
import { filterProducts } from '../../services/helperService'
import { useAppUserDefaultStore } from '../../stores/appUserDefaultStore'


export interface SourcingComponentProps {
  source: 'import' | 'orderhistory' | 'report'
  sortedProducts: Product[]
  selectedPairs: { parentId: string; product: Product }[]
  productsMap: Record<string, Product>
  optionsBundle: Record<string, OptionsBundle>
  selectedOptionIdMap: Record<string, Product>
  fileMetadata: any
  editId?: string
  hasFetchedOptions: boolean
  hasOptions: boolean
  loading: boolean
  isModalOpen: boolean
  pageTitle: string
  handleGoBack: () => void
  onGetOptions: () => void
  onSaveClick: () => void
  onSummaryClick: () => void
  onQuantityChange: (articleId: string, value: string) => void
  onEdit: (id: string) => void
  onSelectOption: (opt: Product) => void
  onCancelEdit: () => void
  onCloseModal: () => void
  searchOptions: Product[]
  searchLoading: boolean
  onSearch: (term: string) => Promise<Product[]>
  onUpdateOptions: (opts: Product[]) => void
  onDeleteProduct?: (id: string) => void
  saveModalMode: 'create' | 'update'
  reportId?: string
  restaurantId?: string
}

export default function SourcingComponent({
  source,
  sortedProducts,
  selectedPairs,
  productsMap,
  optionsBundle,
  selectedOptionIdMap,
  fileMetadata,
  editId,
  hasFetchedOptions,
  hasOptions,
  loading,
  isModalOpen,
  pageTitle,
  handleGoBack,
  onGetOptions,
  onSaveClick,
  onSummaryClick,
  onQuantityChange,
  onEdit,
  onSelectOption,
  onCancelEdit,
  onCloseModal,
  searchOptions,
  searchLoading,
  onSearch,
  onUpdateOptions,
  onDeleteProduct,
  saveModalMode,
  reportId,
  restaurantId,
}: SourcingComponentProps) {
  const { t } = useTranslation()

  const initialOrderRef = React.useRef<string[]>([]);
  if (initialOrderRef.current.length === 0) {
    initialOrderRef.current = sortedProducts.map(p => p.articleId);
  }

  const orderedProducts = initialOrderRef.current
    .map(id => productsMap[id])
    .filter(Boolean) as Product[];

  const filterValue = useAppUserDefaultStore(s => s.filter)


  const filteredProducts = filterProducts(orderedProducts, filterValue)

  // const filteredProductsMap = filterProductMap(productsMap, filterValue)
  // const filteredSelectedPairs = filterProductMap(selectedOptionIdMap, filterValue)
  const filteredProductsMap = productsMap
  const filteredSelectedPairs = selectedOptionIdMap
  
  if (sortedProducts.length === 0) {
    handleGoBack()
    return null
  }

  return (
    <>
      <div className="max-w-full p-2 min-[500px]:p-0 min-[500px]:max-w-7xl mx-auto mb-6 flex items-center justify-between">
        {/* left side */}
        <div className="flex items-center">
          <button
            onClick={handleGoBack}
            aria-label={t('import.goBack')}
            className="flex items-center"
          >
            <ArrowLeft className="transition hover:scale-110 w-10 h-6 text-text dark:text-text-dark" />
          </button>
          <h1 className="text-2xl font-bold text-text dark:text-text-dark pl-1">
            {editId ? t('sourcing.editTitle') : pageTitle}
          </h1>
        </div>

        {!editId && (
          <>
              {/* right side */}
              <div className="flex items-center space-x-3 font-semibold">
              {hasFetchedOptions ? (
                <>
                  <button
                    onClick={onSaveClick}
                    disabled={loading}
                    className={greyButtonClass}
                  >
                    {source === 'report' ? (
                      <>
                        <span className="md:hidden">{t('sourcing.save')}</span>
                        <span className="hidden md:inline">{t('reports.saveChanges')}</span>
                      </>
                    ) : (
                      t('sourcing.save')
                    )}
                  </button>
                  <button
                    onClick={onSummaryClick}
                    disabled={loading}
                    className={greenButtonClass}
                  >
                    {t('sourcing.summary')}
                  </button>
                </>
              ) : (
                <button
                  onClick={onGetOptions}
                  disabled={loading}
                  className={greenButtonClass}
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : hasOptions ? (
                    t('sourcing.refresh')
                  ) : (
                    t('sourcing.getOptions')
                  )}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      <AnimatePresence mode="wait">
        {editId ? (
          <motion.div
            key="edit"
            initial={{ opacity: 0, x: 50 }}  
            animate={{ opacity: 1, x: 0 }}   
            exit={{ opacity: 0, x: 100 }}  
            transition={{ duration: 0.2 }}
          >
            <SourcingEdit
              product={productsMap[editId]!}
              options={optionsBundle[editId]?.sourcingOptions || []}
              selectedOption={selectedOptionIdMap[editId]!}
              onCancel={onCancelEdit}
              onSelect={onSelectOption}
              searchOptions={searchOptions}
              searchLoading={searchLoading}
              onSearch={onSearch}
              onUpdateOptions={onUpdateOptions}
            />
          </motion.div>
        ) : (
          <motion.div
            key="compare"
            initial={{ opacity: 0, x: -50 }}  
            animate={{ opacity: 1, x: 0 }}   
            exit={{ opacity: 0, x: -100 }}    
            transition={{ duration: 0.2 }}
          >
            <div className="max-w-7xl mx-auto mb-6">
              <ComparisonSummary productsMap={filteredProductsMap} selectedMap={filteredSelectedPairs} />
            </div>
            <SourcingComparison
              products={filteredProducts}
              selectedPairs={selectedPairs}
              hasOptions={hasOptions}
              loading={loading}
              onQuantityChange={onQuantityChange}
              onEdit={onEdit}
              onDeleteProduct={onDeleteProduct}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <SaveReportModal
        isOpen={isModalOpen}
        onClose={onCloseModal}
        fileMetadata={fileMetadata}
        sourcingData={{
          productsIdMap: productsMap,
          optionsBundle,
          selectedOptionIdMap,
        }}
        mode={saveModalMode}
        reportId={reportId}
        restaurantId={restaurantId}
      />
    </>
  )
}
